/**
 * Shared button styles — Modern Material Enterprise (DesignTest/DESIGN.md).
 * Pill radius, near-black primary (light) / light primary (dark).
 */
import { getMm } from "../../theme/modernMaterialEnterprise";

export function createSystemButtonStyles(isDarkMode) {
  const mm = getMm(isDarkMode);

  const base = {
    fontFamily: mm.fontBody,
    fontWeight: 500,
    textTransform: "none",
    borderRadius: mm.radiusFull,
    boxShadow: "none",
    letterSpacing: "0.1px",
    transition: "background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
  };

  const primaryButtonSx = {
    ...base,
    backgroundColor: mm.primary,
    color: mm.onPrimary,
    border: `1px solid ${mm.primary}`,
    "&:hover": {
      backgroundColor: mm.primaryDark,
      borderColor: mm.primaryDark,
      boxShadow: mm.elev1,
    },
    "&:disabled": {
      backgroundColor: mm.surfaceContainer,
      borderColor: mm.surfaceContainer,
      color: mm.onSurfaceVariant,
    },
  };

  const cancelButtonSx = {
    ...base,
    color: mm.primary,
    border: `1px solid ${mm.outline}`,
    backgroundColor: "transparent",
    "&:hover": {
      backgroundColor: mm.hoverWash,
      borderColor: mm.outline,
      boxShadow: "none",
    },
  };

  const outlineButtonSx = {
    ...base,
    color: mm.primary,
    borderColor: mm.outline,
    borderWidth: 1,
    "&:hover": {
      borderColor: mm.outline,
      backgroundColor: mm.hoverWash,
    },
    "&.Mui-disabled": {
      borderColor: mm.outlineVariant,
      color: mm.onSurfaceVariant,
    },
  };

  const secondaryButtonSx = {
    ...base,
    color: mm.onPrimaryContainer,
    backgroundColor: mm.primaryContainer,
    border: `1px solid ${mm.primaryContainer}`,
    "&:hover": {
      backgroundColor: mm.primaryContainer,
      boxShadow: mm.elev1,
    },
  };

  const destructiveButtonSx = {
    ...base,
    color: mm.onError,
    backgroundColor: mm.error,
    border: `1px solid ${mm.error}`,
    "& .MuiSvgIcon-root": { color: mm.onError },
    "&:hover": {
      backgroundColor: isDarkMode ? "#FFDAD6" : "#93000A",
      borderColor: isDarkMode ? "#FFDAD6" : "#93000A",
      boxShadow: "none",
      color: mm.onError,
      "& .MuiSvgIcon-root": { color: isDarkMode ? "#690005" : mm.onError },
    },
    "&:disabled": {
      backgroundColor: mm.surfaceContainer,
      borderColor: mm.surfaceContainer,
      color: mm.onSurfaceVariant,
      "& .MuiSvgIcon-root": { color: mm.onSurfaceVariant },
    },
  };

  const destructiveIconButtonSx = {
    ...destructiveButtonSx,
    minWidth: 40,
    width: 40,
    height: 40,
    p: "6px",
  };

  const greenSmileyButtonSx = {
    ...base,
    color: isDarkMode ? "#A5D6A7" : "#1B5E20",
    backgroundColor: isDarkMode ? "rgba(76, 175, 80, 0.22)" : "rgba(76, 175, 80, 0.14)",
    border: `1px solid ${isDarkMode ? "rgba(76, 175, 80, 0.5)" : "rgba(76, 175, 80, 0.4)"}`,
    "&:hover": {
      backgroundColor: isDarkMode ? "rgba(76, 175, 80, 0.3)" : "rgba(76, 175, 80, 0.22)",
      borderColor: "#4CAF50",
      boxShadow: "none",
    },
  };

  const redSmileyButtonSx = {
    ...base,
    color: isDarkMode ? "#FFABAB" : "#B91C1C",
    backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.2)" : "rgba(239, 68, 68, 0.1)",
    border: `1px solid ${isDarkMode ? "rgba(239, 68, 68, 0.5)" : "rgba(239, 68, 68, 0.35)"}`,
    "&:hover": {
      backgroundColor: isDarkMode ? "rgba(239, 68, 68, 0.28)" : "rgba(239, 68, 68, 0.16)",
      borderColor: "#EF4444",
      boxShadow: "none",
    },
  };

  const iconActionSx = {
    ...secondaryButtonSx,
    minWidth: 34,
    width: 34,
    height: 34,
    p: 0,
    color: mm.onSurfaceVariant,
    backgroundColor: "transparent",
    border: "none",
    "&:hover": {
      backgroundColor: mm.hoverWash,
      color: mm.primary,
      boxShadow: "none",
    },
  };

  const iconActionTableSx = {
    ...iconActionSx,
    p: "6px",
  };

  const iconActionLargeSx = {
    ...iconActionSx,
    width: 56,
    height: 56,
    transition: "background-color 0.15s ease, transform 0.2s ease",
    "&:hover": {
      backgroundColor: mm.hoverWash,
      transform: "scale(1.02)",
      boxShadow: "none",
    },
  };

  const toolbarButtonSx = {
    ...outlineButtonSx,
    minHeight: 34,
    py: 0.5,
    px: 1.25,
    fontSize: "0.8125rem",
    lineHeight: 1.2,
    width: { xs: "100%", sm: "auto" },
    "& .MuiButton-startIcon": {
      marginRight: "6px",
      "& svg": { fontSize: "1.05rem" },
    },
  };

  const dashboardArrowButtonSx = {
    ...base,
    minWidth: 36,
    width: 36,
    height: 36,
    p: 0,
    color: mm.primary,
    backgroundColor: mm.primaryContainer,
    border: `1px solid ${mm.outlineVariant}`,
    "&:hover": {
      backgroundColor: mm.hoverWash,
      boxShadow: "none",
      transform: "translateX(2px)",
    },
    "& svg": { fontSize: "1.125rem" },
  };

  const dialogPrimarySx = {
    ...primaryButtonSx,
    fontSize: { xs: "0.9rem", sm: "1rem" },
    px: { xs: 2, sm: 3 },
    py: 1.25,
  };

  const dialogCancelSx = {
    ...cancelButtonSx,
    fontSize: { xs: "0.9rem", sm: "1rem" },
    px: { xs: 2, sm: 3 },
    py: 1.25,
  };

  const dialogFormPrimarySx = {
    ...primaryButtonSx,
    fontSize: "0.875rem",
    px: 3,
    py: { xs: 1.5, sm: 1 },
  };

  const dialogFormCancelSx = {
    ...cancelButtonSx,
    fontSize: "0.875rem",
    px: 3,
    py: { xs: 1.5, sm: 1 },
    "&.Mui-disabled": {
      borderColor: mm.outlineVariant,
      color: mm.onSurfaceVariant,
      backgroundColor: "transparent",
    },
  };

  const dialogFormDestructiveSx = {
    ...destructiveButtonSx,
    fontSize: "0.875rem",
    px: 3,
    py: { xs: 1.5, sm: 1 },
  };

  const destructiveOutlineButtonSx = {
    ...base,
    color: mm.error,
    borderColor: mm.error,
    borderWidth: 1,
    backgroundColor: "transparent",
    "&:hover": {
      borderColor: mm.error,
      backgroundColor: isDarkMode ? "rgba(255, 180, 171, 0.12)" : "rgba(186, 26, 26, 0.08)",
      boxShadow: "none",
    },
    "&.Mui-disabled": {
      borderColor: mm.outlineVariant,
      color: mm.onSurfaceVariant,
    },
  };

  const warningButtonSx = {
    ...base,
    color: mm.onTertiary,
    backgroundColor: mm.tertiaryContainer,
    border: `1px solid ${mm.tertiaryContainer}`,
    "&:hover": {
      backgroundColor: mm.tertiary,
      borderColor: mm.tertiary,
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: mm.surfaceContainer,
      borderColor: mm.surfaceContainer,
      color: mm.onSurfaceVariant,
    },
  };

  return {
    primaryButtonSx,
    cancelButtonSx,
    outlineButtonSx,
    secondaryButtonSx,
    destructiveButtonSx,
    destructiveIconButtonSx,
    destructiveOutlineButtonSx,
    greenSmileyButtonSx,
    redSmileyButtonSx,
    iconActionSx,
    iconActionTableSx,
    iconActionLargeSx,
    toolbarButtonSx,
    dashboardArrowButtonSx,
    dialogPrimarySx,
    dialogCancelSx,
    dialogFormPrimarySx,
    dialogFormCancelSx,
    dialogFormDestructiveSx,
    warningButtonSx,
    buttonSx: secondaryButtonSx,
  };
}

/** MUI theme component overrides — keeps default Buttons aligned with system styles app-wide */
export function getMuiThemeButtonComponents(isDarkMode) {
  const {
    primaryButtonSx,
    destructiveButtonSx,
    outlineButtonSx,
    cancelButtonSx,
    secondaryButtonSx,
  } = createSystemButtonStyles(isDarkMode);
  const mm = getMm(isDarkMode);

  return {
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          fontFamily: mm.fontBody,
          fontWeight: 500,
          textTransform: "none",
          borderRadius: mm.radiusFull,
          boxShadow: "none",
          "&:hover": { boxShadow: mm.elev1 },
        },
        containedPrimary: { ...primaryButtonSx },
        containedError: { ...destructiveButtonSx },
        containedSecondary: { ...secondaryButtonSx },
        outlined: { ...outlineButtonSx },
        text: { ...cancelButtonSx },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: mm.radiusFull,
          boxShadow: "none",
          transition: "background-color 0.15s ease, color 0.15s ease",
        },
        colorInherit: {
          border: "none",
          backgroundColor: "transparent",
          "&:hover": { backgroundColor: mm.hoverWash },
        },
        colorDefault: {
          border: `1px solid ${mm.outlineVariant}`,
          backgroundColor: mm.surfaceLowest,
          "&:hover": {
            backgroundColor: mm.hoverWash,
            boxShadow: "none",
          },
        },
      },
    },
  };
}

/** @deprecated use createSystemButtonStyles */
export const createMentorListButtonStyles = createSystemButtonStyles;
